import { hideConfirmModal, showConfirmModal } from 'redux-store/reducers/modal-reducer';
import { validateDeliverableAttachment } from 'modules/deliverables/reducers/deliverableMediaAttachments';

import { __t } from 'utils/language';
import { CHANNEL_TYPE_INSTAGRAM } from 'utils/channels';
import { trackAnalyticScreenshotOverride } from 'modules/tracking/utils/tracking';

import type { LambdaProxyInstagramAudienceResponse, LambdaProxyTikTokAudienceResponse } from 'types';

function isValidLambdaMetrics(
  metricsResponse?: LambdaProxyInstagramAudienceResponse | LambdaProxyTikTokAudienceResponse
): boolean {
  if (!metricsResponse) {
    return false;
  }

  if (metricsResponse.assetType === 'influencer_instagram_audience_analytics') {
    return Boolean(metricsResponse.metrics?.ageSplit.length)
      || Boolean(metricsResponse.metrics?.genderSplit.length)
      || Boolean(metricsResponse.metrics?.countriesSplit.length);
  }

  if (metricsResponse.assetType === 'influencer_tiktok_audience_analytics') {
    return Boolean(metricsResponse.metrics?.genderSplit.length)
      || Boolean(metricsResponse.metrics?.countriesSplit.length);
  }

  return false;
}

export function checkAnalyticsScreenshots<T extends { url: string }>(
  files: Array<T>,
  feedbackModal: React.ReactNode,
  channel: string
) {
  return async (dispatch): Promise<Array<T>> => {
    const type = (channel === CHANNEL_TYPE_INSTAGRAM)
      ? 'influencer_instagram_audience_analytics'
      : 'influencer_tiktok_audience_analytics';
    const lambdaResponses = await Promise.all(
      files.map((file) =>
        validateDeliverableAttachment(file, type)
      )
    );
    const invalidImageResponses = lambdaResponses.filter(response => !isValidLambdaMetrics(response));

    const canContinue = await new Promise<boolean>((resolve) => {
      // cancel upload
      if (invalidImageResponses.length > 0) {
        dispatch(showConfirmModal({
          innerContent: feedbackModal,
          confirmButtonLabel: __t('deliverable_media_attachments.button_discard'),
          onConfirm: async () => {
            dispatch(hideConfirmModal());
            resolve(false);
          },
          proceedButtonLabel: __t('deliverable_media_attachments.button_upload'),
          onProceed: async () => {
            dispatch(hideConfirmModal());
            trackAnalyticScreenshotOverride();
            resolve(true);
          }
        }));
      } else {
        resolve(true);
      }
    });

    if (canContinue) {
      return files;
    }

    return [];
  };
}
